// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { Sans14, spacing, typography } from "@recidiviz/design-system";
import { scaleTime } from "d3-scale";
import { startOfDay } from "date-fns";
import { observer } from "mobx-react-lite";
import { rem } from "polished";
import React from "react";
import styled from "styled-components";

import { palette } from "~design-system";

import { useRootStore } from "../../components/StoreProvider";
import { formatDateRange, formatWorkflowsDate } from "../../utils";
import { Client, JusticeInvolvedPerson } from "../../WorkflowsStore";
import { Resident } from "../../WorkflowsStore/Resident";
import WorkflowsOfficerName from "../WorkflowsOfficerName";
import { Supervision } from "./ClientDetailSidebarComponents/Supervision";
import { UsTnNavigateToTepeFormButton } from "./ClientDetailSidebarComponents/US_TN";
import { Incarceration } from "./ResidentDetailSidebarComponents/Incarceration";
import { ClientProfileProps, ResidentProfileProps } from "./types";

const Wrapper = styled.div``;

const VizHeader = styled.div`
  ${typography.Sans14}
  display: flex;
  justify-content: space-between;

  & > *:first-child {
    margin-right: ${rem(spacing.lg)};
  }
`;

const StateSpecificWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-top: ${rem(spacing.sm)};
`;

const Title = styled.div`
  color: ${palette.pine2};
  margin-bottom: ${rem(spacing.xs)};
`;

const OfficerAssignment = styled(Sans14)`
  flex: 0 0 auto;
`;

const LifeSentenceAdmissionDate = styled.span`
  color: ${palette.signal.links};
`;

const TimelineDates = styled(Sans14)`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;

  & > *:first-child {
    margin-right: ${rem(spacing.sm)};
    text-align: left;
  }

  & > div {
    flex: 1 0 auto;
    text-align: right;
  }

  span {
    color: ${palette.signal.links};
  }
`;

const TimelineChart = styled.svg`
  height: ${rem(9)};
  margin-bottom: ${rem(spacing.sm)};
  margin-top: ${rem(spacing.md)};
  overflow: visible;
  width: 100%;
`;

const TimelineSentence = styled.line`
  stroke-width: ${rem(1)};
`;

const EmptyTimeline = styled(TimelineSentence)`
  stroke-width: ${rem(2.5)};
`;

const TimelineToday = styled.circle`
  r: ${rem(4.5)};
`;

export function formatSentenceLength(
  startDate: Date,
  expirationDate: Date,
): string {
  return formatDateRange(startDate, expirationDate);
}

export function formatTimeToGo(expirationDate: Date): string {
  const today = startOfDay(new Date());
  const lastDay = startOfDay(expirationDate);

  return `${formatDateRange(today, lastDay)} ${
    today > lastDay ? "past end" : "to go"
  }`;
}

type TimelineLabels = {
  start: string;
  end: string;
};

export const LifeSentenceDisplay = ({
  admissionDate,
}: {
  admissionDate?: Date;
}) => {
  return (
    <Sans14>
      Serving a life sentence since{" "}
      <LifeSentenceAdmissionDate>
        {formatWorkflowsDate(admissionDate)}
      </LifeSentenceAdmissionDate>
    </Sans14>
  );
};

export const OfficerAssignmentDisplay = ({
  officerId,
}: {
  officerId?: string;
}) => {
  if (!officerId) {
    return null;
  }

  return (
    <OfficerAssignment>
      Assigned to <WorkflowsOfficerName officerId={officerId} />
    </OfficerAssignment>
  );
};

function StateSpecificSupervisionDetails({
  client,
}: ClientProfileProps): React.ReactElement<any> | null {
  switch (client.stateCode) {
    case "US_TN":
      return <UsTnNavigateToTepeFormButton client={client} />;
    default:
      return null;
  }
}

export const usMoFallback = ({
  header,
  startDate,
  endDate,
  timelineLabels,
  today,
  officerId,
}: {
  header: string;
  startDate?: Date;
  endDate?: Date;
  timelineLabels: TimelineLabels;
  today: Date;
  officerId?: string;
}): React.ReactElement<any> => {
  return (
    <Wrapper className="SentenceProgress">
      <VizHeader>
        <div>
          <Title>{header}</Title>
          {startDate && (
            <Sans14>{formatSentenceLength(startDate, today)}</Sans14>
          )}
        </div>
        <OfficerAssignmentDisplay officerId={officerId} />
      </VizHeader>
      <TimelineChart>
        <EmptyTimeline
          stroke={palette.slate10}
          x1="0%"
          x2="100%"
          y1="50%"
          y2="50%"
        />
      </TimelineChart>
      {!endDate && !startDate ? (
        <TimelineDates>
          <div>Missing Dates</div>
        </TimelineDates>
      ) : (
        <TimelineDates>
          <div>
            {startDate ? (
              <>
                {timelineLabels.start}:{" "}
                <span>{formatWorkflowsDate(startDate)}</span>
              </>
            ) : (
              "No start date available"
            )}
          </div>
          {endDate ? (
            <div>
              {timelineLabels.end}: <span>{formatWorkflowsDate(endDate)}</span>
            </div>
          ) : (
            "No end date available"
          )}
        </TimelineDates>
      )}
    </Wrapper>
  );
};

export const ProgressTimeline = ({
  header,
  startDate,
  endDate,
  officerId,
  fallbackComponent,
  timelineLabels,
  person,
}: {
  header: string;
  startDate?: Date;
  endDate?: Date;
  officerId?: string;
  fallbackComponent: React.ReactNode;
  timelineLabels: TimelineLabels;
  person: JusticeInvolvedPerson;
}): React.ReactElement<any> => {
  const isMoClient = person instanceof Client && person.stateCode === "US_MO";
  const today = startOfDay(new Date());

  if (!startDate || !endDate || endDate < startDate) {
    if (!isMoClient) return <>{fallbackComponent}</>;

    return usMoFallback({
      header,
      startDate,
      endDate,
      timelineLabels,
      today,
      officerId,
    });
  }
  const expired = endDate < today;

  const timelinePosition = scaleTime()
    .domain([
      startDate,
      // timeline must extend past expiration if it's in the past
      Math.max(endDate.valueOf(), today.valueOf()),
    ])
    .range([0, 100]);

  const timelineOffset = timelinePosition(today) - timelinePosition(endDate);

  return (
    <Wrapper className="SentenceProgress">
      <VizHeader>
        <div>
          <Title>{header}</Title>
          <Sans14>
            {formatSentenceLength(startDate, endDate)} (
            {formatTimeToGo(endDate)})
          </Sans14>
        </div>
        <OfficerAssignmentDisplay officerId={officerId} />
      </VizHeader>

      <TimelineChart>
        <TimelineSentence
          stroke={palette.data.gold1}
          x1={0}
          x2={`${timelinePosition(endDate) - (expired ? 0.5 : 0)}%`}
          y1="50%"
          y2="50%"
        />
        {expired && (
          <>
            <TimelineSentence
              stroke={palette.slate80}
              x1={`${timelinePosition(endDate)}%`}
              x2={`${timelinePosition(endDate)}%`}
              y1="0%"
              y2="100%"
            />
            <TimelineSentence
              stroke={palette.signal.error}
              x1={`${timelinePosition(endDate) + 0.5}%`}
              x2={`${timelinePosition(today)}%`}
              y1="50%"
              y2="50%"
            />
          </>
        )}
        <TimelineToday
          fill={expired ? palette.signal.error : palette.data.gold1}
          cx={`${timelinePosition(today)}%`}
          cy="50%"
        />
      </TimelineChart>

      <TimelineDates
        style={{
          width: `${
            expired && timelineOffset > 10
              ? timelinePosition(endDate) + timelinePosition(today) / 10
              : timelinePosition(endDate)
          }%`,
        }}
      >
        <div>
          {timelineLabels.start}: <span>{formatWorkflowsDate(startDate)}</span>
        </div>
        <div>
          {timelineLabels.end}: <span>{formatWorkflowsDate(endDate)}</span>
        </div>
      </TimelineDates>
      <StateSpecificWrapper>
        <StateSpecificSupervisionDetails client={person as any} />
      </StateSpecificWrapper>
    </Wrapper>
  );
};

export function SupervisionProgress({
  client,
}: ClientProfileProps): React.ReactElement<any> {
  const {
    supervisionStartDate,
    expirationDate,
    assignedStaffId: officerId,
  } = client;

  return (
    <ProgressTimeline
      header="Supervision"
      startDate={supervisionStartDate}
      endDate={expirationDate}
      officerId={officerId}
      fallbackComponent={<Supervision client={client} />}
      timelineLabels={{ start: "Start", end: "End" }}
      person={client}
    />
  );
}

export function IncarcerationProgress({
  resident,
}: ResidentProfileProps): React.ReactElement<any> {
  const {
    admissionDate,
    releaseDate,
    assignedStaffId: officerId,
    onLifeSentence,
  } = resident;

  const {
    tenantStore: {
      labels: { releaseDateCopy },
    },
  } = useRootStore();

  if (onLifeSentence) {
    return (
      <VizHeader>
        <div>
          <Title>Incarceration</Title>
          <LifeSentenceDisplay admissionDate={admissionDate} />
        </div>
        <OfficerAssignmentDisplay officerId={officerId} />
      </VizHeader>
    );
  }

  return (
    <ProgressTimeline
      header="Incarceration"
      startDate={admissionDate}
      endDate={releaseDate}
      officerId={officerId}
      fallbackComponent={<Incarceration resident={resident} />}
      timelineLabels={{ start: "Start", end: releaseDateCopy }}
      person={resident}
    />
  );
}

export const SentenceProgress: React.FC<{
  person: JusticeInvolvedPerson;
}> = observer(function SentenceProgress({ person }) {
  if (person instanceof Resident) {
    return <IncarcerationProgress resident={person} />;
  } else if (person instanceof Client) {
    return <SupervisionProgress client={person} />;
  }
});
